import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearCover,
  describeCover,
  generateCover,
  bookKey,
  getBook,
  getCover,
  setCover,
  uploadCover,
  cropCover,
} from "../api";
import { Link } from "react-router-dom";
import ConfirmDelete from "../components/ConfirmDelete";
import { useT } from "../i18n";
import PictureSheet from "./PictureSheet";

/**
 * CoverPanel is a book's cover: the picture itself, and the way to get one
 * (ADR-0041).
 *
 * The picture is the whole screen's subject, so it is shown at a size worth
 * judging rather than as a thumbnail — a cover is chosen by looking at it.
 * Empty, it is a dashed box of the shape a cover will be, because the shape is
 * fixed and the box is the only honest preview of what is about to arrive.
 *
 * The making of it goes through PictureSheet, the same loop as a scene and a
 * portrait: words for a fraction of a cent, then a picture for about seven.
 */
export default function CoverPanel({
  bookID,
  canDescribe,
  locked = false,
}: {
  bookID: string;
  /** Whether this account may spend on describing and drawing (`book.augment`). */
  canDescribe: boolean;
  /**
   * The cover is derived and may not be hand-edited (ADR-0093). It still shows
   * — a linked book's author has every reason to look at their own cover — and
   * only the controls that would write go.
   */
  locked?: boolean;
}) {
  const t = useT();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);

  const status = useQuery({
    queryKey: ["cover", bookID],
    queryFn: () => getCover(bookID),
    retry: false,
  });
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });

  const changed = () => {
    void client.invalidateQueries({ queryKey: ["cover", bookID] });
    // The library and the book page both carry the cover now.
    void client.invalidateQueries({ queryKey: ["book", bookID] });
    void client.invalidateQueries({ queryKey: ["books"] });
  };
  const remove = useMutation({
    mutationFn: () => clearCover(bookID),
    onSuccess: (fresh) => {
      client.setQueryData(["cover", bookID], fresh);
      changed();
    },
  });

  const cover = status.data?.cover;
  // A cover is always upright (illustration.CoverAspectRatio), so the empty box
  // is the shape the picture will be and nothing reflows when it arrives.
  const shape = status.data?.shape ?? "3:4";

  return (
    <div data-testid="cover-panel">
      <p className="mb-3 text-xs text-muted">{t.cover.note}</p>

      <div className="flex flex-wrap items-start gap-4">
        <div
          className="w-40 shrink-0 overflow-hidden rounded-card border border-line bg-sunk"
          style={{ aspectRatio: shape.replace(":", "/") }}
        >
          {cover ? (
            <img
              src={cover.url}
              alt=""
              width={cover.width}
              height={cover.height}
              data-testid="cover-image"
              className="size-full object-cover"
            />
          ) : (
            <div
              data-testid="cover-empty"
              className="grid size-full place-items-center border border-dashed border-line-strong px-2 text-center text-xs text-muted"
            >
              {t.cover.none}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          {locked && <p className="text-xs text-muted">{t.translation.lockedNote}</p>}
          {!locked && (
            <button
              type="button"
              data-testid="cover-change"
              // The sheet starts from the cover's words and cast, which it reads
              // once as it opens: opened before they arrive, it starts blank.
              disabled={status.isPending}
              onClick={() => setOpen(true)}
              className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {cover ? t.cover.change : t.cover.add}
            </button>
          )}
          {cover && !locked && (
            <div>
              <ConfirmDelete
                testId="cover-remove"
                label={t.cover.remove}
                disabled={remove.isPending}
                question={t.cover.removeQuestion}
                onConfirm={() => remove.mutate()}
              />
            </div>
          )}
          {/* The description a generated cover was drawn from. Worth keeping on
              screen: it is where the next attempt starts, and it is the only
              record of why the picture looks the way it does. */}
          {/* Whose faces it keeps (ADR-0053). */}
          {(cover?.drawn_from.length ?? 0) > 0 && (
            <p data-testid="cover-drawn-from" className="text-xs text-muted">
              <span dir="auto">{t.images.drawnFrom(cover!.drawn_from)}</span>
            </p>
          )}
          {cover?.prompt && (
            <p
              dir="auto"
              data-testid="cover-prompt"
              className="rounded-control border-s-2 border-sage bg-sunk px-2.5 py-1.5 text-xs leading-6 text-ink-soft"
            >
              {cover.prompt}
            </p>
          )}
          {/* Who can be put on the cover by name, and the link to go and give
              them a face. Said here rather than only inside the sheet, because
              it is the thing to do *before* starting. */}
          {(status.data?.cast.missing.length ?? 0) > 0 && (
            <p data-testid="cover-missing-faces" className="text-xs text-muted">
              <span dir="auto">{t.cover.missingFaces(status.data!.cast.missing)}</span>{" "}
              <Link to={`/studio/books/${bookID}/characters`} className="text-sage-deep underline">
                {t.cover.toCharacters}
              </Link>
            </p>
          )}
        </div>
      </div>

      {remove.isError && (
        <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
          {t.cover.removeFailed}
        </p>
      )}

      {open && (
        <PictureSheet
          kind="cover"
          title={t.cover.title}
          /* What the cover is of: the book, in the author's own words. */
          subject={[book.data?.title, book.data?.description].filter(Boolean).join(" · ")}
          prices={status.data?.draw_prices ?? []}
          canDescribe={canDescribe}
          onDescribe={(v) => describeCover(bookID, v)}
          onGenerate={(prompt, _shape, cast, _beside, quality) =>
            generateCover(bookID, { prompt, character_ids: cast, quality })
          }
          castBookID={bookID}
          onUpload={(file) => uploadCover(bookID, file)}
          onCrop={(key, region) => cropCover(bookID, key, region)}
          current={status.data?.cover}
          charactersHref={`/studio/books/${bookID}/characters`}
          onUse={async (candidate) => {
            const fresh = await setCover(bookID, {
              object_key: candidate.object_key,
              prompt: candidate.prompt,
              source: candidate.prompt ? "generated" : "upload",
              quality: candidate.quality,
              character_ids: candidate.character_ids ?? [],
            });
            client.setQueryData(["cover", bookID], fresh);
            changed();
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
